<template>
  <div class="sidebar__tags sidebar__wrapper">
    <div class="sidebar__body">
      <slot />
      <col-selector
        v-model="sharedFirstLevel"
        :class="'first-level'"
        :items="firstLevelTags"
        :col-layout="'groupped'"
        :app-slot="appSlot"
      >
        <template v-slot:groupped="slotProps">
          <div class="sidebar__tag-family">
            <div
              :class="[
                'sidebar__tag-family__icon',
                'icon-' + slotProps.item.label[lng].toLowerCase(),
              ]"
              :style="{
                backgroundImage: 'url(' + slotProps.item.imageURL + ')',
              }"
            />
            <div class="sidebar__tag-family__name">{{ slotProps.item.label[lng] }}</div>
          </div>
        </template>
      </col-selector>
    </div>
    <div v-if="indicators.length || searching" class="sidebar__floating-cols">
      <div class="tag-selector__title">
        <div class="tag-selector__close-btn" @click="close" />
        Choose Indicator or Tag
      </div>
      <div class="sidebar__floating-col">
        <col-selector
          v-model="sharedSecondLevel"
          :class="'second-level'"
          :items="secondLevelTags"
          :col-layout="'tags'"
          :app-slot="appSlot"
        />
        <col-selector
          v-if="indicators.length || searching"
          v-model="indId"
          :class="'indicators'"
          :items="thirdLevelIndicators"
          :col-layout="'list'"
          :app-slot="appSlot"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ColSelector from '@/components/Sidebar/ColSelector.vue'

import { mergeBreakdowns } from '@/utils/query.js'
import isFlipped from '../../mixins/isFlipped'

export default {
  components: { ColSelector },
  mixins: [isFlipped],
  model: {
    prop: 'selection',
    event: 'selection',
  },
  props: ['appSlot', 'indicators', 'searching', 'selection'],
  computed: {
    mode() {
      return this.$route.params.mode
    },
    mainCubeId() {
      return this.$store.state.query.mainCubeId
    },
    sharedFirstLevel: {
      get() {
        return this.selection.firstLevel
      },
      set(tagId) {
        this.$emit('selection', { firstLevel: tagId })
      },
    },
    sharedSecondLevel: {
      get() {
        return this.selection.secondLevel
      },
      set(tagId) {
        this.$emit('selection', { ...this.selection, secondLevel: tagId })
      },
    },
    firstLevelTags() {
      return this.$store.getters.tagType('firstLevel').map(tag => {
        return {
          label: tag.tagName,
          id: tag.id,
          imageURL: tag.imageURL ? `${process.env.VUE_APP_CMS_IMAGE_URL}/${tag.imageURL}` : '',
          group: tag.parent?.tagName,
          disabled:
            this.$store.getters.tagIndicators(tag.id).filter(ind => {
              return this.isFlipped && this.mode === 'compare'
                ? ind.cube.cubeMetaId === this.mainCubeId
                : true
            }).length === 0,
        }
      })
    },
    secondLevelTags() {
      return this.$store.getters
        .tagType('secondLevel')
        .filter(tag => {
          return (
            tag.parent.id === this.selection.firstLevel && tag.parent.tagName.en !== tag.tagName.en
          )
        })
        .map(tag => {
          return {
            label: tag.tagName,
            id: tag.id,
          }
        })
        .sort((tag1, tag2) => {
          return tag1.label[this.lng].toLowerCase() <= tag2.label[this.lng].toLowerCase() ? -1 : 1
        })
    },
    thirdLevelIndicators() {
      if (this.indicators.length) {
        let inds = this.indicators.map(d => ({
          label: d.label,
          id: d.indicatorId,
          metaId: d.metaId,
          cubeMetaId: d.cube.cubeMetaId
        }))

        //sort only if not searching
        if (!this.searching) {
          return inds.sort((ind1, ind2) => {
            return ind1.label[this.lng].toLowerCase() < ind2.label[this.lng].toLowerCase() ? -1 : 1
          })
        } else {
          return inds
        }
      } else if (this.searching) {
        return [{ label: { en: 'No matching indicators', fr: 'No matching indicators' }, id: 0 }]
      } else {
        return []
      }
    },
    indId: {
      get() {
        return this.$store.state.query[this.appSlot + 'IndId']
      },
      set(id) {
        const ind = this.indicators.find(d => d.indicatorId === id)
        if (this.$store.state.query[this.appSlot + 'CubeId'] !== ind.cube.cubeMetaId) {
          // When the new indicator is from diferent cube of the last indicator
          this.$store.commit(this.appSlot + 'CubeId', ind.cube.cubeMetaId)
          if (this.appSlot === 'main') {
            this.$store.commit('flip', false)
            if (/DF_NDSC/i.test(ind.cube.cubeMetaId)) {
              this.$store.commit('chart', 'heatmap')
            }
          }
        } else {
          // Check breakdowns consistency
          this.$store.commit(
            this.appSlot + 'Breakdowns',
            mergeBreakdowns(
              this.$store.state.query[this.appSlot + 'Breakdowns'],
              this.$store.state.cubeMeta[this.appSlot + 'BreakdownDimensions'] || []
            )
          )
        }
        this.$store.commit(this.appSlot + 'IndId', ind.metaId || 'cube')
        this.close()
      },
    },
  },
  mounted() {
    this.$root.$on('sidebar:tag', tagId => {
      this.$emit('tag:recovery', this.firstLevelTags.find(tag => tag.id === tagId).id)
    })
  },
  methods: {
    close() {
      this.$emit('close', this.isMobile)
    },
  },
}
</script>

<style lang="stylus" scoped>
.sidebar__tag-family
  display: flex
  align-items: center

.sidebar__tag-family__icon
  margin-right: vw(10px)
  width: vw(40px)
  height: vw(40px)
  background-position: center center
  background-size: contain
  background-repeat: no-repeat
  flex-shrink: 0

  .mobile &
    +m-breakpoint(landscape)
      width: vh(40px)
      height: vh(40px)

.sidebar__tag-family__name
  m-font-size(14, 18)
  margin-top: 0.2em

  .mobile &
    font-size: 0.8em
    line-height: 1.25

.tag-selector__close-btn
  m-icon("breadcrumb", 30)
  position: absolute
  top: 50%
  left: vw(20px)
  cursor: pointer
  transform: translateY(-50%)

.tag-selector__title
  m-font-size(18, 22)
  position: relative
  display: flex
  justify-content: center
  align-items: center
  padding: vw(25vw) vw(75px) vw(25vw) vw(50px)
  border-bottom: 1px solid $blue
  color: $blue
  text-align: center
  font-weight: 700
  background: white

  +m-breakpoint(md)
    display: none

.sidebar__floating-cols
  position: absolute
  top: 0
  left: 50%
  height: 100%
  width: unit((1 / 3) * 100, "vw")
  display: flex
  flex-direction: column

  .mobile &
    left: 0
    width: 100vw

    +m-breakpoint(landscape)
      width: 50vw
      left: 50vw

.sidebar__floating-col
  display: flex
  flex-direction: column
  flex: 1
  overflow-y: auto

  .col-selector
    width: 100%
    height: min-content

    &:last-child
      flex: 1 1 0%
</style>
